// RotaProfissional (formerly Start Astral), vocational map & career transition
// Route: /rota-profissional

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Share2, Sparkles, ChevronRight, ArrowLeft, ChevronDown, Clock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../hooks/useI18n';
import { safeInvoke } from '../lib/safeInvoke';
import { supabase } from '../lib/supabase';
import { TextSkeleton } from '../components/shared/TextSkeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { usePricing } from '../contexts/PricingContext';

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

type PageMode = 'vocacional' | 'transicao';

interface VocacionalReport {
  perfil_vocacional:      string;
  talentos_competencias:  string[];
  desafios_carreira:      string[];
  ambientes_ideais:       string[];
  estilo_lideranca:       string;
  estilo_colaboracao:     string;
  areas_profissionais:    Array<{ titulo: string; motivo: string }>;
  caminhos_desafiadores:  string[];
  ciclos_importantes:     string;
  badge?: { mc_sign: string; sun_house: number | string; saturn_sign: string; north_node: string };
}

interface TransicaoReport {
  mapa_vocacional:         VocacionalReport;
  analise_carreira_atual:  { sintese: string; talentos_aplicados: string[]; competencias: string[]; dificuldades: string[] };
  analise_cargo_almejado:  { sintese: string; alinhamento_mapa: string; oportunidades: string[]; desafios: string[] };
  processo_transicao:      { facilidades: string; desafios_transicao: string; sugestoes: string[]; previsao_proximos_3_meses: string };
}

interface DbReport {
  id: string;
  mode: 'vocacional' | 'transicao';
  report_json: VocacionalReport | TransicaoReport;
  badge_json: VocacionalReport['badge'] | null;
  current_career: string | null;
  target_career: string | null;
  generated_at: string;
}

// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────

function whatsappShare(text: string) {
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
}

function ShareBtn({ section, text }: { section: string; text: string }) {
  return (
    <button className="share-button" onClick={() => whatsappShare(`✨ ${section} (Rota Profissional)\n\n${text.slice(0, 280)}...\n\nAnalise seu mapa vocacional em Professya`)} title="Compartilhar no WhatsApp">
      <Share2 size={14} /> Compartilhar
    </button>
  );
}

function StringList({ items }: { items: string[] }) {
  return (
    <ul className="report-list">
      {items.map((item, i) => (
        <li key={i} className="report-list-item">
          <span className="report-list-bullet">→</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

// Numbered card list, same layout as Áreas Profissionais.
// Splits each item on ', ' (em-dash with spaces): part before = bold title (card-title),
// part after = description (card-reason). If no separator, shows full text as description.
function CardList({ items }: { items: string[] }) {
  return (
    <div className="careers-list">
      {items.map((item, i) => {
        const sepIdx = item.indexOf(', ');
        const title  = sepIdx > 0 ? item.slice(0, sepIdx).trim()      : null;
        const desc   = sepIdx > 0 ? item.slice(sepIdx + 3).trim() : item;
        return (
          <div key={i} className="career-card">
            <div className="career-number">{i + 1}</div>
            <div className="career-info">
              {title && <h3 className="career-title">{title}</h3>}
              <p className="career-reason">{desc}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TechBadge({ badge }: { badge?: VocacionalReport['badge'] }) {
  if (!badge) return null;

  const isKnown = (v: string | number | null | undefined) =>
    v != null && String(v).trim() !== '' && String(v).toLowerCase() !== 'unknown' && String(v) !== '?';

  const parts: string[] = [];
  if (isKnown(badge.mc_sign))    parts.push(`MC em ${badge.mc_sign}`);
  if (isKnown(badge.sun_house))  parts.push(`Sol Casa ${badge.sun_house}`);
  if (isKnown(badge.saturn_sign)) parts.push(`Saturno em ${badge.saturn_sign}`);
  if (isKnown(badge.north_node)) parts.push(`Nodo Norte em ${badge.north_node}`);

  if (parts.length === 0) return null;

  return (
    <div className="rota-profissional-badge">
      <Star size={14} className="badge-star" />
      <span>{parts.join(' · ')}</span>
    </div>
  );
}

// ─────────────────────────────────────────
// Mode selection screen
// ─────────────────────────────────────────

function ModeSelector({ onSelect, vocExists, transicaoCount }: {
  onSelect: (mode: PageMode) => void;
  vocExists: boolean;
  transicaoCount: number;
}) {
  return (
    <div className="rota-profissional-landing">
      <div className="rota-profissional-hero">
        <div className="rota-profissional-hero-icon">🧭</div>
        <h1 className="rota-profissional-hero-title">Rota Profissional</h1>
        <p className="rota-profissional-hero-subtitle">O que você está buscando?</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '480px', margin: '0 auto', width: '100%' }}>
        <button className="rota-profissional-mode-card" onClick={() => onSelect('vocacional')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '2.5rem' }}>🌟</div>
            {vocExists && (
              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: 'rgba(74,222,128,0.12)', color: '#4ade80', fontWeight: 600 }}>
                ✓ Gerado
              </span>
            )}
          </div>
          <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
            Mapa Vocacional
          </h2>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Descubra seus talentos, competências, estilo de liderança, ambientes ideais e as áreas profissionais mais alinhadas ao seu mapa natal.
          </p>
          <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--color-gold)', fontSize: 'var(--text-caption)', fontWeight: 600 }}>
            {vocExists ? 'Ver meu mapa ✦' : 'Gerar Mapa Vocacional'} <ChevronRight size={14} />
          </div>
        </button>

        <button className="rota-profissional-mode-card" onClick={() => onSelect('transicao')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '2.5rem' }}>🔄</div>
            {transicaoCount > 0 && (
              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: 'rgba(96,165,250,0.12)', color: '#60a5fa', fontWeight: 600 }}>
                {transicaoCount} análise{transicaoCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
            Transição de Carreira
          </h2>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Análise da sua carreira atual, compatibilidade com o cargo almejado, e um plano de transição baseado nos seus trânsitos planetários dos próximos 3 meses.
          </p>
          <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--color-gold)', fontSize: 'var(--text-caption)', fontWeight: 600 }}>
            Nova análise <ChevronRight size={14} />
          </div>
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Vocacional Report view
// ─────────────────────────────────────────

function VocacionalView({ report }: { report: VocacionalReport }) {
  type Tab = 'talentos' | 'desafios' | 'ambientes' | 'lideranca' | 'areas' | 'caminhos' | 'ciclos';
  const [tab, setTab] = useState<Tab>('talentos');
  const { t } = useI18n();

  const tabs: { key: Tab; icon: string; tKey: string }[] = [
    { key: 'talentos',  icon: '💪', tKey: 'tabTalentos' },
    { key: 'desafios',  icon: '🔥', tKey: 'tabDesafios' },
    { key: 'ambientes', icon: '🌿', tKey: 'tabAmbientes' },
    { key: 'lideranca', icon: '👑', tKey: 'tabLideranca' },
    { key: 'areas',     icon: '🎯', tKey: 'tabAreas' },
    { key: 'caminhos',  icon: '⚡', tKey: 'tabCaminhos' },
    { key: 'ciclos',    icon: '⏰', tKey: 'tabCiclos' },
  ];

  return (
    <div className="rota-profissional-report">
      <div className="report-hero">
        <h1 className="report-hero-title">Mapa Vocacional</h1>
        <TechBadge badge={report.badge} />
      </div>

      {report.perfil_vocacional && (
        <div className="report-section" style={{ marginBottom: '1.5rem' }}>
          <div className="report-section-actions">
            <h2>Perfil Vocacional</h2>
            <ShareBtn section="Perfil Vocacional" text={report.perfil_vocacional} />
          </div>
          <p className="report-prose">{report.perfil_vocacional}</p>
        </div>
      )}

      <div className="report-tabs" style={{ flexWrap: 'wrap' }}>
        {tabs.map(tb => (
          <button key={tb.key} className={`report-tab ${tab === tb.key ? 'active' : ''}`} onClick={() => setTab(tb.key)}>
            <span>{tb.icon}</span><span>{t(`pages.startAstral.${tb.tKey}`)}</span>
          </button>
        ))}
      </div>

      <div className="report-content">
        {tab === 'talentos' && (
          <div className="report-section">
            <div className="report-section-actions">
              <h2>Talentos e Competências</h2>
              <ShareBtn section="Talentos" text={report.talentos_competencias.join('\n\n')} />
            </div>
            <CardList items={report.talentos_competencias} />
          </div>
        )}
        {tab === 'desafios' && (
          <div className="report-section">
            <div className="report-section-actions">
              <h2>Desafios de Carreira</h2>
              <ShareBtn section="Desafios" text={report.desafios_carreira.join('\n\n')} />
            </div>
            <CardList items={report.desafios_carreira} />
          </div>
        )}
        {tab === 'ambientes' && (
          <div className="report-section">
            <div className="report-section-actions">
              <h2>Ambientes Ideais</h2>
              <ShareBtn section="Ambientes" text={report.ambientes_ideais.join('\n\n')} />
            </div>
            <CardList items={report.ambientes_ideais} />
          </div>
        )}
        {tab === 'lideranca' && (
          <div className="report-section">
            <div style={{ marginBottom: '1.5rem' }}>
              <div className="report-section-actions">
                <h2>Estilo de Liderança</h2>
                <ShareBtn section="Liderança" text={report.estilo_lideranca} />
              </div>
              <p className="report-prose">{report.estilo_lideranca}</p>
            </div>
            <div>
              <div className="report-section-actions">
                <h2>Estilo de Colaboração</h2>
                <ShareBtn section="Colaboração" text={report.estilo_colaboracao} />
              </div>
              <p className="report-prose">{report.estilo_colaboracao}</p>
            </div>
          </div>
        )}
        {tab === 'areas' && (
          <div className="report-section">
            <div className="report-section-actions">
              <h2>Áreas Profissionais Sugeridas</h2>
              <ShareBtn section="Áreas" text={report.areas_profissionais.map(a => a.titulo).join(', ')} />
            </div>
            <div className="careers-list">
              {report.areas_profissionais.map((area, i) => (
                <div key={i} className="career-card">
                  <div className="career-number">{i + 1}</div>
                  <div className="career-info">
                    <h3 className="career-title">{area.titulo}</h3>
                    <p className="career-reason">{area.motivo}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === 'caminhos' && (
          <div className="report-section">
            <div className="report-section-actions">
              <h2>Caminhos Desafiadores</h2>
              <ShareBtn section="Caminhos Desafiadores" text={report.caminhos_desafiadores.join('\n\n')} />
            </div>
            <CardList items={report.caminhos_desafiadores} />
          </div>
        )}
        {tab === 'ciclos' && (
          <div className="report-section">
            <div className="report-section-actions">
              <h2>Ciclos Profissionais Importantes</h2>
              <ShareBtn section="Ciclos" text={report.ciclos_importantes} />
            </div>
            <p className="report-prose">{report.ciclos_importantes}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Transição Report view
// ─────────────────────────────────────────

function TransicaoView({ report, onNewAnalysis }: { report: TransicaoReport; onNewAnalysis: () => void }) {
  type Tab = 'vocacional' | 'atual' | 'almejada' | 'transicao';
  const [tab, setTab] = useState<Tab>('vocacional');
  const { t } = useI18n();

  const tabs: { key: Tab; icon: string; tKey: string }[] = [
    { key: 'vocacional', icon: '🌟', tKey: 'tabVocacional' },
    { key: 'atual',      icon: '💼', tKey: 'tabAtual' },
    { key: 'almejada',   icon: '🎯', tKey: 'tabAlmejada' },
    { key: 'transicao',  icon: '🔄', tKey: 'tabTransicao' },
  ];

  const voc = report.mapa_vocacional;
  const atu = report.analise_carreira_atual;
  const alm = report.analise_cargo_almejado;
  const tra = report.processo_transicao;

  return (
    <div className="rota-profissional-report">
      <div className="report-hero">
        <h1 className="report-hero-title">Transição de Carreira</h1>
        <TechBadge badge={voc.badge} />
      </div>

      <div className="report-tabs">
        {tabs.map(tb => (
          <button key={tb.key} className={`report-tab ${tab === tb.key ? 'active' : ''}`} onClick={() => setTab(tb.key)}>
            <span>{tb.icon}</span><span>{t(`pages.startAstral.${tb.tKey}`)}</span>
          </button>
        ))}
      </div>

      <div className="report-content">
        {tab === 'vocacional' && (
          <div className="report-section">
            {voc.perfil_vocacional && <p className="report-prose" style={{ marginBottom: '1.5rem' }}>{voc.perfil_vocacional}</p>}
            <h3 style={{ marginBottom: '0.75rem' }}>Talentos e Competências</h3>
            <CardList items={voc.talentos_competencias} />
            <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Áreas Profissionais Sugeridas</h3>
            <div className="careers-list">
              {voc.areas_profissionais.map((a, i) => (
                <div key={i} className="career-card">
                  <div className="career-number">{i + 1}</div>
                  <div className="career-info"><h3 className="career-title">{a.titulo}</h3><p className="career-reason">{a.motivo}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === 'atual' && (
          <div className="report-section">
            <div className="report-section-actions"><h2>Análise da Carreira Atual</h2><ShareBtn section="Carreira Atual" text={atu.sintese} /></div>
            <p className="report-prose">{atu.sintese}</p>
            <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Talentos Aplicados</h3>
            <StringList items={atu.talentos_aplicados} />
            <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Competências Desenvolvidas</h3>
            <StringList items={atu.competencias} />
            <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Dificuldades e Fricções</h3>
            <StringList items={atu.dificuldades} />
          </div>
        )}
        {tab === 'almejada' && (
          <div className="report-section">
            <div className="report-section-actions"><h2>Cargo Almejado</h2><ShareBtn section="Cargo Almejado" text={alm.sintese} /></div>
            <p className="report-prose">{alm.sintese}</p>
            <div className="report-planet-block" style={{ marginTop: '1rem' }}>
              <h3>Alinhamento com o Mapa Natal</h3>
              <p>{alm.alinhamento_mapa}</p>
            </div>
            <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Oportunidades Astrológicas</h3>
            <StringList items={alm.oportunidades} />
            <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Desafios a Superar</h3>
            <StringList items={alm.desafios} />
          </div>
        )}
        {tab === 'transicao' && (
          <div className="report-section">
            <div className="report-planet-block">
              <h3>Facilidades da Transição</h3>
              <p>{tra.facilidades}</p>
            </div>
            <div className="report-planet-block" style={{ marginTop: '1rem' }}>
              <h3>Desafios da Transição</h3>
              <p>{tra.desafios_transicao}</p>
            </div>
            <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Sugestões de Desenvolvimento</h3>
            <StringList items={tra.sugestoes} />
            <div className="report-planet-block" style={{ marginTop: '1.5rem' }}>
              <h3>Previsão, Próximos 3 Meses para o Trabalho</h3>
              <p>{tra.previsao_proximos_3_meses}</p>
            </div>
            <ShareBtn section="Plano de Transição" text={tra.facilidades + ' ' + tra.desafios_transicao} />
          </div>
        )}
      </div>

      <div className="report-regenerate">
        <button className="btn btn-secondary btn-sm" onClick={onNewAnalysis}>
          <Sparkles size={14} /> Nova análise de transição
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Generation form (after mode selected)
// ─────────────────────────────────────────

function GenerationForm({
  mode,
  onGenerate,
  onBack,
  isUnlimited,
}: {
  mode: PageMode;
  onGenerate: (currentCareer?: string, targetCareer?: string) => void;
  onBack: () => void;
  isUnlimited: boolean;
}) {
  const { formatProductPrice, loading: pricingLoading } = usePricing();
  const navigate = useNavigate();
  const [currentCareer, setCurrentCareer] = useState('');
  const [targetCareer,  setTargetCareer]  = useState('');

  const isVoc = mode === 'vocacional';

  function handleCta() {
    if (!isUnlimited) {
      navigate('/wallet');
      return;
    }
    onGenerate(currentCareer || undefined, targetCareer || undefined);
  }

  return (
    <div className="rota-profissional-landing">
      <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ alignSelf: 'flex-start', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <ArrowLeft size={14} /> Voltar
      </button>

      <div className="rota-profissional-hero">
        <div className="rota-profissional-hero-icon">{isVoc ? '🌟' : '🔄'}</div>
        <h1 className="rota-profissional-hero-title">{isVoc ? 'Mapa Vocacional' : 'Transição de Carreira'}</h1>
        <p className="rota-profissional-hero-subtitle">
          {isVoc
            ? 'Análise profunda dos seus talentos, vocação e caminhos profissionais alinhados ao seu mapa natal'
            : 'Compare sua carreira atual com o cargo almejado e receba um plano de transição baseado nos seus astros'}
        </p>
      </div>

      <div className="rota-profissional-benefits-card">
        {isVoc ? (
          <>
            <h2 className="rota-profissional-section-title">O que você recebe</h2>
            <ul className="rota-profissional-benefits-list">
              {[
                'Talentos e competências mapeados pelo mapa natal',
                'Desafios de carreira e como superá-los',
                'Ambientes de trabalho ideais para você',
                'Estilo de liderança e colaboração únicos',
                'Áreas profissionais alinhadas ao seu potencial',
                'Caminhos desafiadores mas possíveis',
                'Ciclos profissionais importantes nos próximos anos',
              ].map((b, i) => (
                <li key={i} className="rota-profissional-benefit-item">
                  <span className="benefit-check">✓</span><span>{b}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <h2 className="rota-profissional-section-title">4 análises completas</h2>
            <ul className="rota-profissional-benefits-list">
              {[
                'Mapa Vocacional completo (8 dimensões)',
                'Análise da carreira atual, talentos, competências e fricções',
                'Compatibilidade do cargo almejado com seu mapa natal',
                'Plano de transição + previsão dos próximos 3 meses',
              ].map((b, i) => (
                <li key={i} className="rota-profissional-benefit-item">
                  <span className="benefit-check">✓</span><span>{b}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {isUnlimited ? (
          <div className="rota-profissional-price-block">
            <span className="rota-profissional-price" style={{ color: '#4ade80' }}>✦ Acesso Ilimitado</span>
            <span className="rota-profissional-price-label">incluído no seu plano, sem custo adicional</span>
          </div>
        ) : (
          <div className="rota-profissional-price-block">
            <span className="rota-profissional-price">{pricingLoading ? '...' : formatProductPrice('start_astral')}</span>
            <span className="rota-profissional-price-label">relatório completo (pagamento único)</span>
          </div>
        )}

        {!isVoc && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
              💼 Sua carreira ou cargo atual
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="Ex: Analista de Marketing, Engenheiro Civil..."
              value={currentCareer}
              onChange={e => setCurrentCareer(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        )}

        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'block', fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
            🎯 {isVoc ? 'Área de interesse ' : 'Cargo ou área almejada '}
            <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{isVoc ? '(opcional)' : ''}</span>
          </label>
          <input
            type="text"
            className="form-input"
            placeholder={isVoc ? 'Ex: psicologia, tecnologia, educação...' : 'Ex: Gerente de Produto, Designer UX...'}
            value={targetCareer}
            onChange={e => setTargetCareer(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>

        <button
          className="btn-primary rota-profissional-cta"
          onClick={handleCta}
          disabled={!isVoc && !targetCareer.trim()}
        >
          <Sparkles size={18} />
          {isUnlimited
            ? (isVoc ? 'Gerar Mapa Vocacional' : 'Gerar Análise de Transição')
            : (isVoc ? 'Comprar e Gerar Mapa' : 'Comprar e Gerar Análise')}
          <ChevronRight size={18} />
        </button>

        <p className="rota-profissional-cta-note">
          Gerado com Claude Opus • Baseado no seu mapa natal
        </p>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '1rem', lineHeight: 1.6, borderTop: '1px solid var(--border-subtle)', paddingTop: '1rem' }}>
          ⚠️ <strong>Aviso legal:</strong> Esta análise tem caráter reflexivo e de entretenimento. Não substitui orientação profissional de carreira ou psicólogos habilitados.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// History section
// ─────────────────────────────────────────

function HistorySection({ reports, onView }: { reports: DbReport[]; onView: (r: DbReport) => void }) {
  const [expanded, setExpanded] = useState(false);
  const PAGE = 5;
  const visible = expanded ? reports : reports.slice(0, PAGE);

  return (
    <div style={{ maxWidth: '480px', margin: '2rem auto 0', width: '100%' }}>
      <p style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
        <Clock size={12} /> Análises anteriores
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {visible.map(r => (
          <button key={r.id} onClick={() => onView(r)}
            style={{ padding: '0.75rem 1rem', backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div>
              <p style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.2rem' }}>
                {r.mode === 'vocacional'
                  ? '🌟 Mapa Vocacional'
                  : `🔄 ${r.current_career ?? ''} → ${r.target_career ?? ''}`}
              </p>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>
                {new Date(r.generated_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            </div>
            <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          </button>
        ))}
      </div>
      {reports.length > PAGE && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          style={{ marginTop: '0.5rem', width: '100%', padding: '0.5rem', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 'var(--text-caption)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
          Ver todas ({reports.length}) <ChevronDown size={13} />
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// Main page
// ─────────────────────────────────────────

export function StartAstral() {
  const { profile } = useAuth();
  const navigate    = useNavigate();

  const [allReports,     setAllReports]     = useState<DbReport[]>([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [viewingReport,  setViewingReport]  = useState<DbReport | null>(null);
  const [mode,           setMode]           = useState<PageMode | null>(null);
  const [generating,     setGenerating]     = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [requiresPayment, setRequiresPayment] = useState(false);

  const vocReport       = allReports.find(r => r.mode === 'vocacional') ?? null;
  const transicaoHistory = allReports.filter(r => r.mode === 'transicao');

  // Load all reports from DB on mount
  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from('start_astral_reports')
      .select('id, mode, report_json, badge_json, current_career, target_career, generated_at')
      .eq('user_id', profile.id)
      .order('generated_at', { ascending: false })
      .then(({ data }) => {
        setAllReports((data ?? []) as unknown as DbReport[]);
        setLoadingReports(false);
      });
  }, [profile?.id]);

  async function reloadReports(): Promise<DbReport[]> {
    if (!profile?.id) return [];
    const { data } = await supabase
      .from('start_astral_reports')
      .select('id, mode, report_json, badge_json, current_career, target_career, generated_at')
      .eq('user_id', profile.id)
      .order('generated_at', { ascending: false });
    const reports = (data ?? []) as unknown as DbReport[];
    setAllReports(reports);
    return reports;
  }

  function handleModeSelect(m: PageMode) {
    if (m === 'vocacional' && vocReport) {
      setViewingReport(vocReport);
    } else {
      setMode(m);
    }
  }

  const handleGenerate = useCallback(async (currentCareer?: string, targetCareer?: string) => {
    if (!profile?.birth_date) { navigate('/birth-chart'); return; }
    setGenerating(true);
    setError(null);
    setRequiresPayment(false);

    type GenResult = { report: unknown; from_cache?: boolean; locked?: boolean };
    const result = await safeInvoke<GenResult>(
      'start-astral',
      {
        body: {
          mode:           mode ?? 'vocacional',
          current_career: currentCareer || undefined,
          target_career:  targetCareer  || undefined,
        },
        timeout: 120_000,
        fallback: null as unknown as GenResult,
      },
    );

    setGenerating(false);

    if (result.fromFallback) {
      const failData = result.data as Record<string, unknown> | null;
      if (failData?.requiresPayment) {
        setRequiresPayment(true);
      } else {
        setError(result.error ?? 'Não foi possível gerar o relatório. Verifique seu mapa natal e tente novamente.');
      }
      return;
    }

    const currentMode = mode ?? 'vocacional';

    // Use report directly from response, more robust than DB reload
    if (result.data?.report) {
      const rawReport = result.data.report as VocacionalReport | TransicaoReport;
      const badge = (result.data.report as Record<string, unknown>).badge as VocacionalReport['badge'] | undefined;
      setViewingReport({
        id:             'generated',
        mode:           currentMode,
        report_json:    rawReport,
        badge_json:     badge ?? null,
        current_career: currentCareer ?? null,
        target_career:  targetCareer  ?? null,
        generated_at:   new Date().toISOString(),
      });
    }

    reloadReports(); // refresh history list in background
    setMode(null);
  }, [profile?.birth_date, profile?.id, mode, navigate]);

  if (!profile?.birth_date) {
    return (
      <div className="page-container">
        <EmptyState emoji="🧭" title="Mapa natal necessário" subtitle="Para gerar sua Rota Profissional, primeiro calcule seu Mapa Natal." cta="Calcular Mapa Natal" ctaAction={() => navigate('/birth-chart')} />
      </div>
    );
  }

  if (loadingReports) return <div className="page-container"><TextSkeleton lines={8} /></div>;

  if (generating) {
    return (
      <div className="page-container rota-profissional-generating">
        <div className="generating-content">
          <Sparkles size={40} className="animate-pulse generating-icon" />
          <h2>Analisando seu mapa natal...</h2>
          <p>{mode === 'transicao' ? 'Gerando análise completa de transição de carreira.' : 'Gerando seu mapa vocacional completo.'}</p>
          <p className="generating-note">Isso pode levar até 1 minuto.</p>
        </div>
      </div>
    );
  }

  // Viewing a report from history
  if (viewingReport) {
    const badge = viewingReport.badge_json ?? undefined;
    return (
      <div className="page-container">
        <button className="btn btn-ghost btn-sm" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          onClick={() => setViewingReport(null)}>
          <ArrowLeft size={14} /> Voltar à Rota Profissional
        </button>
        {viewingReport.mode === 'vocacional' ? (
          <>
            <VocacionalView report={{ ...(viewingReport.report_json as VocacionalReport), badge }} />
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '1.5rem', lineHeight: 1.6, textAlign: 'center' }}>
              ⚠️ Esta análise tem caráter reflexivo. Não substitui orientação profissional de carreira.
            </p>
          </>
        ) : (
          <TransicaoView
            report={viewingReport.report_json as TransicaoReport}
            onNewAnalysis={() => { setViewingReport(null); setMode('transicao'); }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="page-container">
      {error && <div className="error-banner">{error}</div>}

      {requiresPayment && (
        <div className="card" style={{ marginBottom: '1rem', borderColor: 'rgba(245,200,66,0.3)', textAlign: 'center' }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Pagamento necessário</p>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Adquira um plano ou crédito para gerar esta análise.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/wallet')}>Ver planos ✦</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setRequiresPayment(false)}>Cancelar</button>
          </div>
        </div>
      )}

      {mode === null ? (
        <>
          <ModeSelector
            onSelect={handleModeSelect}
            vocExists={!!vocReport}
            transicaoCount={transicaoHistory.length}
          />
          {allReports.length > 0 && (
            <HistorySection reports={allReports} onView={setViewingReport} />
          )}
        </>
      ) : (
        <GenerationForm
          mode={mode}
          isUnlimited={profile.unlimited_access}
          onGenerate={handleGenerate}
          onBack={() => { setMode(null); setError(null); setRequiresPayment(false); }}
        />
      )}
    </div>
  );
}
